import { useLenis } from 'lenis/react'
import { useEffect, useRef } from 'react'
import { Button, Icon } from '@/components/ui'
import { footer } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { clamp, damp } from '@/lib/math'
import { pointer } from '@/lib/pointer'
import { createRectCache } from '@/lib/rectCache'
import { drift, onTick, useMaskedReveal } from '@/sections/Call/motion'
import styles from './Footer.module.css'

/** The wordmark's weight range on Technor's `wght` axis (never `wdth`: there is none). */
const REST_WEIGHT = 240
const PEAK_WEIGHT = 900
/** Extra tracking (em) at the peak of the swell. */
const PEAK_TRACKING = 0.035

/**
 * The footer, on cool mist glass. A giant VOKARTA spans the width; each letter swells along
 * Technor's weight axis (and opens its tracking slightly) as the pointer nears, damped like
 * a sound wave passing, with a faint prism sheen following the cursor. On touch the letters
 * breathe on a slow drift; under reduced motion they rest. Then the closing lines, links,
 * the legal line and a back-to-top button that glides home with Lenis.
 */
export function Footer() {
  const rootRef = useRef<HTMLElement>(null)
  const markRef = useRef<HTMLSpanElement>(null)
  const reduced = useReducedMotion()
  const finePointer = useMediaQuery(MEDIA.hover)
  const inView = useInView(rootRef, { rootMargin: '10% 0px' })
  const lenis = useLenis()
  useMaskedReveal(rootRef)

  // Fit the wordmark to the container width at its resting weight.
  useEffect(() => {
    const mark = markRef.current
    const frame = mark?.parentElement
    if (!mark || !frame) return
    // Only a width change refits. Refitting changes the frame's height, and reacting to that
    // would loop the observer.
    let fittedWidth = -1
    const fit = (force = false) => {
      const frameStyle = getComputedStyle(frame)
      const available =
        frame.clientWidth - parseFloat(frameStyle.paddingLeft) - parseFloat(frameStyle.paddingRight)
      if (available <= 0 || (!force && available === fittedWidth)) return
      fittedWidth = available
      // Measure at a heavy reference weight, so the swell never pushes past the edges.
      if (!reduced) mark.classList.add(styles.fitting)
      const current = parseFloat(getComputedStyle(mark).fontSize) || 100
      const natural = mark.scrollWidth
      mark.classList.remove(styles.fitting)
      if (natural <= 0) return
      mark.style.fontSize = `${((current * available * 0.97) / natural).toFixed(2)}px`
    }
    fit(true)
    let cancelled = false
    // fonts.ready can resolve before this offscreen face has started loading.
    const family = getComputedStyle(mark).fontFamily.split(',')[0]
    void document.fonts.load(`${reduced ? REST_WEIGHT : 640} 1em ${family}`).then(() => {
      if (!cancelled) fit(true)
    })
    const observer = new ResizeObserver(() => fit())
    observer.observe(frame)
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [reduced])

  // The swell: pointer distance → weight and tracking per letter; drift on touch.
  useEffect(() => {
    const mark = markRef.current
    if (reduced || !inView || !mark) return
    const letters = Array.from(mark.querySelectorAll<HTMLElement>('[data-letter]'))
    const weights = letters.map(() => REST_WEIGHT)
    const written = letters.map(() => -1)
    let centers: number[] = []
    let letterWidth = 100
    const measure = () => {
      // Rest-state centres, relative to the wordmark; close enough while letters swell.
      const box = mark.getBoundingClientRect()
      centers = letters.map((letter) => {
        const rect = letter.getBoundingClientRect()
        return rect.left + rect.width / 2 - box.left
      })
      letterWidth = box.width / Math.max(letters.length, 1)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(mark)

    let sheenX = Number.NaN
    let sheenY = 0.5
    let presence = 0

    // The letters change weight every frame, so measuring the word each tick would force a
    // text layout per frame. Re-measure while the pointer moves, else a few times a second.
    const markRect = createRectCache()
    const remove = onTick((dt, time) => {
      const box = markRect(mark)!
      const sigmaX = letterWidth * 1.05
      const sigmaY = box.height * 1.6
      const tracked = finePointer && pointer.lastMove > 0
      const dy = pointer.y - (box.top + box.height / 2)
      presence = damp(presence, tracked ? Math.exp(-((dy / sigmaY) ** 2)) : 0, 3, dt)

      for (let i = 0; i < letters.length; i++) {
        let swell: number
        if (tracked) {
          const dx = pointer.x - (box.left + centers[i])
          swell = Math.exp(-((dx / sigmaX) ** 2)) * presence
        } else {
          // Touch: each letter breathes on its own slow drift.
          swell = (0.5 + 0.5 * drift(time * 0.32, i * 1.37)) * 0.55
        }
        weights[i] = damp(weights[i], REST_WEIGHT + (PEAK_WEIGHT - REST_WEIGHT) * swell, 4, dt)
        const weight = Math.round(weights[i])
        if (weight !== written[i]) {
          written[i] = weight
          const t = (weight - REST_WEIGHT) / (PEAK_WEIGHT - REST_WEIGHT)
          letters[i].style.fontWeight = String(weight)
          letters[i].style.letterSpacing = `${(t * PEAK_TRACKING).toFixed(4)}em`
        }
      }

      // The prism sheen rides with the cursor (or drifts along the word on touch).
      const targetX = tracked
        ? clamp((pointer.x - box.left) / Math.max(box.width, 1), -0.3, 1.3)
        : 0.5 + 0.45 * drift(time * 0.09, 4)
      const targetY = tracked ? clamp((pointer.y - box.top) / Math.max(box.height, 1), -1, 2) : 0.5
      if (Number.isNaN(sheenX)) sheenX = targetX
      sheenX = damp(sheenX, targetX, 2.6, dt)
      sheenY = damp(sheenY, targetY, 2.6, dt)
      const strength = tracked ? presence : 0.4
      mark.style.setProperty('--sx', `${(sheenX * 100).toFixed(2)}%`)
      mark.style.setProperty('--sy', `${(sheenY * 100).toFixed(2)}%`)
      mark.style.setProperty('--sheen', strength.toFixed(3))
    })

    return () => {
      remove()
      observer.disconnect()
    }
  }, [reduced, inView, finePointer])

  const backToTop = () => {
    const focusMain = () =>
      document.querySelector<HTMLElement>('#main')?.focus({ preventScroll: true })
    if (lenis) {
      lenis.scrollTo(0, {
        duration: reduced ? 0 : 2.2,
        immediate: reduced,
        easing: (t) => (t < 0.5 ? 8 * t ** 4 : 1 - (-2 * t + 2) ** 4 / 2),
        onComplete: focusMain,
      })
    } else {
      window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
      focusMain()
    }
  }

  return (
    <footer ref={rootRef} id="footer" className={styles.root} data-theme="mist">
      <div className={cx('container', styles.top)}>
        <p className={styles.line} data-reveal>
          {/* One sentence per line: "Your business." / "Your assistant." / "Always available." */}
          {footer.line.split(/(?<=\.)\s+/).map((sentence) => (
            <span key={sentence} className={styles.sentence}>
              {sentence}{' '}
            </span>
          ))}
        </p>
        <p className={styles.closing} data-reveal>
          {footer.closing}
        </p>
      </div>

      <div className={cx('container', styles.bar)}>
        <ul className={styles.links} role="list">
          {footer.links.map((link) => (
            <li key={link.href}>
              <Button
                variant="link"
                href={link.href}
                external={'external' in link ? link.external : undefined}
                icon={'external' in link && link.external ? 'arrow-up-right' : undefined}
                className={styles.link}
              >
                {link.label}
              </Button>
            </li>
          ))}
        </ul>
        <button type="button" className={styles.toTop} onClick={backToTop} data-cursor="link">
          <span className={styles.toTopLabel}>{footer.backToTop}</span>
          <span className={styles.toTopIcon} aria-hidden="true">
            <Icon name="arrow-down" />
          </span>
        </button>
      </div>

      <p className={styles.wordmark} aria-hidden="true">
        <span ref={markRef} className={cx(styles.letters, reduced && styles.still)}>
          {Array.from(footer.wordmark).map((letter, index) => (
            <span key={index} className={styles.letter} data-letter>
              {letter}
            </span>
          ))}
        </span>
      </p>

      <div className={cx('container', styles.meta)}>
        <p>{footer.legal}</p>
        <p>{footer.madeIn}</p>
      </div>
    </footer>
  )
}
